import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Paper,
  Alert,
  Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import DonationsTable from './DonationsTable';
import ChangePasswordDialog from './ChangePasswordDialog';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import authService from '../../utils/authService';

interface Stats {
  totalSuccessful: number;
  totalAmount: number;
}

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const navigate = useNavigate();
  const token = authService.getAccessToken() || '';

  const fetchStats = async () => {
    try {
      const res = await authService.authFetch(`${API_URL}/api/admin/stats`);
      if (res.status === 401) {
        authService.clearTokens();
        navigate('/admin/login');
        return;
      }
      if (!res.ok) {
        let msg = res.statusText;
        try {
          const errData = await res.json();
          msg = errData.message || msg;
        } catch (_) {
          if (res.status === 401) msg = 'Oturum süresi doldu. Tekrar giriş yapın.';
        }
        throw new Error(msg);
      }
      const data = (await res.json()) as Stats;
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleLogout = async () => {
    try {
      await authService.authFetch(`${API_URL}/api/admin/logout`, { method: 'POST' });
    } catch (_) {
      // Ignore errors, clear tokens anyway
    }
    authService.clearTokens();
    navigate('/admin/login');
  };

  return (
    <Box>
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>  {/* Darker blue for better contrast */}
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Admin Panel
          </Typography>
          <Tooltip title="Parola Değiştir">
            <IconButton color="inherit" onClick={() => setPasswordDialogOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Çıkış Yap">
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box p={3} sx={{ backgroundColor: '#121212', minHeight: '100vh' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!stats && !error && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
        {stats && (
          <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
            <Paper sx={{ 
              p: 2, 
              flex: '1 1 260px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              '& *': { color: 'white !important' }
            }}>
              <Typography variant="h6">Başarılı Bağış Sayısı</Typography>
              <Typography variant="h4">{stats.totalSuccessful}</Typography>
            </Paper>
            <Paper sx={{ 
              p: 2, 
              flex: '1 1 260px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              '& *': { color: 'white !important' }
            }}>
              <Typography variant="h6">Bağış Tutarı (₺)</Typography>
              <Typography variant="h4">
                {stats.totalAmount.toLocaleString('tr-TR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Box>
        )}
        <DonationsTable />
      </Box>

      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        token={token}
      />
    </Box>
  );
};

export default AdminDashboardPage;
